<template>
  <section class="sidebar-nav-section" :aria-labelledby="`sidebar-section-${section.id}`">
    <h2 :id="`sidebar-section-${section.id}`" class="sidebar-nav-section__label">
      {{ section.label }}
    </h2>
    <ul class="sidebar-nav-section__list">
      <li
        v-for="item in section.items"
        :key="item.key"
        class="sidebar-nav-section__item"
      >
        <router-link :to="navTarget(item)" custom v-slot="{ href }">
          <component
            :is="item.isDisabled ? 'span' : 'a'"
            :href="item.isDisabled ? undefined : href"
            :class="[
              ...linkClass(item, activeStates[item.key]),
              {
                'sidebar-nav-link--has-rail-badge': isRailMode && item.badge,
                'sidebar-nav-link--action-rail': isRailMode && item.badge && item.showAlertDot
              }
            ]"
            :data-nav-path="item.path"
            :aria-current="activeStates[item.key] ? 'page' : undefined"
            :aria-disabled="item.isDisabled ? 'true' : undefined"
            :aria-label="item.ariaLabel || item.label"
            v-tooltip.right="tooltipLabel(item)"
            @click="handleClick(item, $event)"
          >
            <span
              class="sidebar-nav-link__icon"
              :class="{
                'sidebar-nav-link__icon--has-alert':
                  item.showAlertDot && !(isRailMode && item.badge)
              }"
              aria-hidden="true"
            >
              <i :class="['pi', 'pi-fw', item.icon]" />
            </span>
            <span
              v-if="isRailMode && item.badge"
              class="sidebar-nav-link__rail-badge"
              aria-hidden="true"
            >
              {{ formatBadge(item.badge) }}
            </span>
            <span class="sidebar-nav-link__text">
              {{ item.label }}
            </span>
            <span
              v-if="item.statusBadge"
              class="sidebar-nav-badge sidebar-nav-badge--status"
            >
              {{ item.statusBadge }}
            </span>
            <span
              v-else-if="item.badge"
              class="sidebar-nav-badge sidebar-nav-badge--count"
            >
              {{ formatBadge(item.badge) }}
            </span>
          </component>
        </router-link>
      </li>
    </ul>
  </section>
</template>

<script>
import { isRouteActive } from '@/features/hr-workspace/utils/shellNavActive';

export default {
  name: 'SidebarNavSection',
  props: {
    section: {
      type: Object,
      required: true
    },
    isRailMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['navigate'],
  computed: {
    currentUserId() {
      return this.$store.state.users?.user?.id ?? null;
    },
    activeStates() {
      const route = this.$route;
      const states = {};

      for (const item of this.section.items || []) {
        states[item.key] = isRouteActive(item.path, route.path, {
          routeName: item.routeName,
          activeRouteName: route.name,
          relatedRouteNames: item.relatedRouteNames,
          routeParams: route.params,
          currentUserId: this.currentUserId,
          navKey: item.key,
          queryTab: route.query?.tab
        });
      }

      return states;
    }
  },
  methods: {
    navTarget(item) {
      if (item.to) return item.to;
      if (item.key === 'my-profile') {
        return { name: 'my-profile' };
      }
      return item.path;
    },
    linkClass(item, isActive) {
      return [
        'sidebar-nav-link',
        {
          'sidebar-nav-link--active': isActive,
          'sidebar-nav-link--disabled': item.isDisabled
        }
      ];
    },
    tooltipLabel(item) {
      if (this.isRailMode) return item.railTooltip || item.label;
      return item.label.length > 24 ? item.label : null;
    },
    formatBadge(value) {
      const count = Number(value);
      if (!Number.isFinite(count)) return String(value);
      return count > 99 ? '99+' : String(count);
    },
    handleClick(item, event) {
      if (item.isDisabled) {
        event?.preventDefault();
        return;
      }
      // Let RouterLink preserve browser shortcuts for opening a separate tab/window.
      if (event?.metaKey || event?.ctrlKey || event?.shiftKey || event?.altKey || event?.button > 0) {
        return;
      }
      event?.preventDefault();
      this.$emit('navigate', item, event);
    }
  }
};
</script>
